<template>
  <ContentContainer :loadingStatus="isLoading">
    <div class="contract-content">
      <div class="contract content-wrapper">
        <el-row>
          <el-col :span="12">
            <div class="page-header">
              <el-button type="text" @click="goToContractListPage">
                &ll; {{ $t('back') }}
              </el-button>
            </div>
          </el-col>
          <el-col :span="12" style="text-align: right;">
            <el-button type="primary" @click="onShowPreviewButtonClick()">{{ $t('previewContract') }}</el-button>
          </el-col>
        </el-row>
        <div class="contract-container">
          <contract-menu
            @onClickContractMenu="onShowContractContainer">
          </contract-menu>
          <div class="tab-contract-container">
            <div v-show="isShowContainer.general">
              <tab-general-component
                v-model="contractDetail"
                :hotel="hotel"
                :sale-person-list="salePersonList"
                :ref-code-format="companyData.contract.refCodeFormat"
                :market-list="marketList">
              </tab-general-component>
            </div>
            <div v-show="isShowContainer.agent">
              <tab-agent-component
                v-model="contractDetail"
                :agent-list="agentList"
                :is-show-from-agent="isShowFromAgent">
              </tab-agent-component>
            </div>
            <div v-show="isShowContainer.rateCode">
              <tab-rate-code-component
                v-model="contractDetail"
                :room-type-list="roomTypeList"
                :rate-code-list="rateCodeList"
                :default-remark-list="remarkList">
              </tab-rate-code-component>
            </div>
            <div v-show="isShowContainer.allotment">
              <tab-allotment-component
                v-model="contractDetail"
                :room-type-list="roomTypeList"
              >
              </tab-allotment-component>
            </div>
            <div v-show="isShowContainer.promotion">
              <tab-promotion-component
                v-model="contractDetail"
                :promotion-list="promotionList"
                :default-remark-list="remarkList">
              </tab-promotion-component>
            </div>
            <div v-show="isShowContainer.supplement">
              <tab-supplement-component
                v-model="contractDetail"
                :default-remark-list="remarkList"
                :supplement-list="supplementList"
                :supplement-type-list="supplementTypeList">
              </tab-supplement-component>
            </div>
            <div v-show="isShowContainer.transfer">
              <tab-transfer-component
                v-model="contractDetail"
                :default-remark-list="remarkList"
                :transfer-list="transferList">
              </tab-transfer-component>
            </div>
            <div v-show="isShowContainer.termsOfPayment" class="table-contract-container">
              <tab-terms-of-payment-component
                v-model="contractDetail"
                :terms-of-payment-list="termsOfPaymentList">
              </tab-terms-of-payment-component>
            </div>
          </div>
        </div>
      </div>
      <div class="contract">
        <el-form ref="form" label-width="0" style="margin-top: -30px">
          <el-form-item>
            <el-button v-if="contractDetail.status === CONTRACT_STATUS.DRAFT"
              type="info"
              style="width: 100%;"
              @click="onSaveDraftButtonClick">
              {{ $t('saveDraft') }}
            </el-button>
            <el-button v-if="contractDetail.status === CONTRACT_STATUS.PUBLISHED"
                       type="primary"
                       style="width: 100%;"
                       @click="onSaveButtonClick">
              {{ $t('saveContract') }}
            </el-button>
          </el-form-item>
        </el-form>
      </div>
    </div>
    <preview-contract
      :visible="isShowPreview"
      :hotel="hotel"
      :contract-detail="contractDetail"
      :supplement-type-list="supplementTypeList"
      :company-data="companyData"
      @onClose="isShowPreview = false"
      @onDownloadStart="startLoading"
      @onDownloadSuccess="stopLoading"
      @onDownloadError="onDownloadError"
      @onSaveDraft="onPreviewSaveDraft"
      @onSavePublish="onPreviewSavePublish"
      ></preview-contract>
  </ContentContainer>
</template>

<script>
import i18nMessages from '@/translations/Contract/ManageContract/Edit.i18n'
import AlertViewMixin from '@/mixins/alert-view-mixin'
import LoadingViewMixin from '@/mixins/loading-view-mixin'
import NotificationMixin from '@/mixins/notification-mixin'
import ContentContainer from '@/components/ContentContainerComponent.vue'
import ContractMenu from '@/views/Contract/components/ManageContract/ContractMenu'
import TabGeneralComponent from '@/views/Contract/components/ManageContract/TabGeneralComponent'
import TabAgentComponent from '@/views/Contract/components/ManageContract/TabAgentComponent'
import TabRateCodeComponent from '@/views/Contract/components/ManageContract/TabRateCodeComponent'
import TabAllotmentComponent from '@/views/Contract/components/ManageContract/TabAllotmentComponent'
import TabPromotionComponent from '@/views/Contract/components/ManageContract/TabPromotionComponent'
import TabSupplementComponent from '@/views/Contract/components/ManageContract/TabSupplementComponent'
import TabTransferComponent from '@/views/Contract/components/ManageContract/TabTransferComponent'
import TabTermsOfPaymentComponent from '@/views/Contract/components/ManageContract/TabTermsOfPaymentComponent'
import ContractApi from '@/api/contract-api'
import AgentContactPersonApi from '@/api/agent/contact-person-api' // ---------> TODO: Remove when merge to version
import PreviewContract from '@/views/Contract/components/ManageContract/PreviewContract'
import { get } from 'lodash'
import { CONTRACT_STATUS, VUEX_EVENT } from '@/configs/constant'
import { genRefCode } from '@/domains/contract-domain'
import { sortPeriodList } from '@/helpers/datetime'
import { mapState } from 'vuex'
import { filterDisableList } from '@/helpers'

export default {
  name: 'ContractEdit',
  mixins: [AlertViewMixin, LoadingViewMixin, NotificationMixin],
  components: {
    ContentContainer,
    ContractMenu,
    TabGeneralComponent,
    TabAgentComponent,
    TabRateCodeComponent,
    TabPromotionComponent,
    TabSupplementComponent,
    TabTransferComponent,
    TabTermsOfPaymentComponent,
    TabAllotmentComponent,
    PreviewContract
  },
  i18n: { messages: i18nMessages },
  props: {
    contractId: {
      type: Number,
      default: null
    },
    agentList: {
      type: Array,
      default: () => {
        return []
      }
    },
    hotel: {
      type: Object,
      default: () => {
        return {}
      }
    },
    salePersonList: {
      type: Array,
      default: () => {
        return []
      }
    },
    isShowFromAgent: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapState({
      contractListCurrentPage: state => state.contract.contractListCurrentPage,
      contractPageListStore: state => state.contract.contractPageListStore
    })
  },
  created: async function () {
    if (!this.contractId) {
      return this.$router.push({
        name: 'ContractList'
      })
    }

    try {
      this.isLoading = true
      this.agentList = filterDisableList(this.agentList)
      const result = await ContractApi.detail(this.contractId)
      this.contractDetail = result.contractDetail
      this.promotionList = filterDisableList(result.promotionList)
      this.roomTypeList = filterDisableList(result.roomTypeList)
      this.supplementList = result.supplementList
      this.supplementTypeList = result.supplementTypeList
      this.transferList = filterDisableList(result.transferList)
      this.termsOfPaymentList = filterDisableList(result.termsOfPaymentList)
      this.companyData = result.companyData
      this.marketList = result.marketList
      this.remarkList = result.remarkList

      const rateCodeList = result.rateCodeList
      const rateDetailList = result.rateDetailList

      this.rateCodeList = _initRateCode(rateCodeList, rateDetailList, this.roomTypeList)
      this.rateCodeList.sort((prev, next) => (prev.code > next.code) ? 1 : -1)
    } catch (err) {
      this.alertError(err.message)
    }

    this.isLoading = false
  },
  data () {
    return {
      isLoading: false,
      promotionList: [],
      contractDetail: {
        hotelId: '',
        refCode: '',
        startDate: '',
        endDate: '',
        salePerson: {},
        remark: '',
        internalNote: '',
        hotelCode: '',
        agent: {},
        status: CONTRACT_STATUS.DRAFT,
        rateCodeList: [],
        promotionList: [],
        allotmentList: [],
        cutOffDateList: [],
        supplementList: [],
        transferList: [],
        termsOfPaymentList: [],
        termAndCondition: {
          updatedAt: '',
          dataList: []
        }
      },
      roomTypeList: [],
      rateCodeList: [],
      supplementList: [],
      supplementTypeList: [],
      transferList: [],
      termsOfPaymentList: [],
      isShowContainer: {
        general: true,
        agent: false,
        rateCode: false,
        promotion: false,
        supplement: false,
        transfer: false,
        termsOfPayment: false
      },
      companyData: {
        code: '',
        companyType: 0,
        contract: {
          refCodeFormat: ''
        },
        currency: 'THB'
      },
      isShowPreview: false,
      CONTRACT_STATUS: CONTRACT_STATUS,
      marketList: [],
      remarkList: []
    }
  },
  methods: {
    onShowContractContainer: function (isShowContainer) {
      this.isShowContainer = isShowContainer
    },
    goToContractListPage: function () {
      let componentName = 'ContractList'
      if (this.isShowFromAgent === true) {
        componentName = 'AgentContractList'
      }
      return this.$router.push({
        name: componentName
      })
    },
    onSaveDraftButtonClick: async function () {
      await this._saveContract()
    },
    onSaveButtonClick: async function () {
      await this._saveContract()
    },
    onPreviewSaveDraft: async function () {
      this.isShowPreview = false
      await this._saveContract()
    },
    onPreviewSavePublish: async function () {
      this.isShowPreview = false
      this.contractDetail.status = CONTRACT_STATUS.PUBLISHED
      await this._saveContract()
    },
    _saveContract: async function () {
      try {
        this.startLoading()
        if (Boolean(this.contractDetail.autoRefCode) === true) {
          let refCodeFormat = get(this.companyData, 'contract.refCodeFormat', '').toUpperCase()
          this.contractDetail.refCode = genRefCode(this.contractDetail, refCodeFormat, this.marketList)
        }

        await this._applyNewAgentData()
        this._sortAllotmentListByPeriod()
        await ContractApi.save(this.contractDetail)
        this.$store.dispatch(VUEX_EVENT.CONTRACT.UPDATE_CONTRACT_PAGE_LIST_STORE, {
          pageNumber: this.contractListCurrentPage,
          contractDetail: this.contractDetail
        })

        let componentName = 'ContractList'
        if (this.isShowFromAgent === true) {
          componentName = 'AgentContractList'
        }

        this.$router.push({
          name: componentName
        })

        const title = this.$t('saveContractSuccessTitle')
        const message = this.$t('saveContractSuccessMessage')

        this.showNotificationSuccess(title, message)
      } catch (err) {
        this.alertError(err.message)
      }

      this.stopLoading()
    },
    onShowPreviewButtonClick: function () {
      if (Boolean(this.contractDetail.autoRefCode) === true) {
        let refCodeFormat = get(this.companyData, 'contract.refCodeFormat', '').toUpperCase()
        this.contractDetail.refCode = genRefCode(this.contractDetail, refCodeFormat, this.marketList)
      }

      const allotmentList = get(this.contractDetail, 'allotmentList', [])
      const sortedAllotmentList = allotmentList.sort(sortPeriodList)
      this.contractDetail.allotmentList = [ ...sortedAllotmentList ]

      this.isShowPreview = true
    },
    onDownloadError: function (errorMessage) {
      this.alertError(errorMessage)
    },
    _sortAllotmentListByPeriod: function () {
      const allotmentList = get(this.contractDetail, 'allotmentList', [])
      allotmentList.sort(sortPeriodList)

      this.contractDetail.allotmentList = [ ...allotmentList ]
    },
    _applyNewAgentData: async function () { // ---------> TODO: Remove when merge to version
      const selectedAgentId = get(this.contractDetail, 'agent.id', '')

      if (selectedAgentId === '') {
        return
      }

      const agentData = this.agentList.find(agent => agent.id === selectedAgentId)

      if (agentData === undefined) {
        return
      }

      const contactPersonList = await AgentContactPersonApi.list(selectedAgentId)
      const selectedContactPersonId = get(this.contractDetail, 'agent.contactPerson.id', '')
      const selectedContactPerson = contactPersonList.find(contactPerson => contactPerson.id === selectedContactPersonId)

      if (selectedContactPerson === undefined) {
        return
      }

      const emailList = get(selectedContactPerson, 'emailList', [])
      const mobileList = get(selectedContactPerson, 'mobileList', [])
      const phone = get(selectedContactPerson, 'phone', '')
      const fax = get(selectedContactPerson, 'fax', '')

      const newContactPersonData = {
        emailList: emailList,
        mobileList: mobileList,
        phone: phone,
        fax: fax
      }
      const newContactPerson = Object.assign({}, selectedContactPerson, newContactPersonData)

      const newAgentContactPerson = {
        contactPerson: newContactPerson
      }
      const newAgent = Object.assign({}, agentData, newAgentContactPerson)
      const newContractAgent = {
        agent: newAgent
      }
      this.contractDetail = Object.assign({}, this.contractDetail, newContractAgent)
    }
  }
}

function _initRateCode (rateCodeList, rateDetailList, roomTypeList) {
  const newRateCodeList = rateCodeList.map((rateCode) => {
    const rateDetail = rateDetailList.filter(rateDetail => rateDetail.rateCodeId === rateCode.id)
    const newRateDetailList = _initRateDetail(rateDetail, roomTypeList)
    const rateDetailObj = {
      rateDetail: newRateDetailList
    }

    const newRateCode = Object.assign({}, rateCode, rateDetailObj)

    return newRateCode
  })

  const result = filterDisableList(newRateCodeList)

  return result
}

function _initRateDetail (rateDetailList, roomTypeList) {
  const newRateDetailList = []

  rateDetailList.forEach((rateDetail) => {
    const roomType = roomTypeList.find(roomType => roomType.id === rateDetail.roomTypeId)

    if (roomType !== undefined) {
      const roomTypeName = get(roomType, 'name', '')
      const roomTypeObj = {
        roomTypeName: roomTypeName
      }

      const newRateDetail = Object.assign({}, rateDetail, roomTypeObj)
      newRateDetailList.push(newRateDetail)
    }
  })

  return newRateDetailList
}
</script>

<style>
  .page-header > small {
    font-size: 14px;
    color: #666;
  }
  .page-header > .el-button {
    font-size: 23px;
  }
  .page-header > .el-button > span > small {
    font-size: 14px;
    color: #666;
  }
  .table-contract-container {
    margin: 0 30px;
    box-shadow: #d4d4d4 0 0 5px;
    border: 1px solid #dddddd;
    border-radius: 5px;
  }
  .contract-container {
    box-shadow: #d4d4d4 0 0 5px;
    padding: 30px;
    border: 1px solid #dddddd;
    border-radius: 5px;
    position: relative;
  }
</style>
